import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';

import { socketUseEffect } from '../../socket';
import { useAuth } from "../../AuthContext";

import {
    IconServer, IconCloud, IconGlobe, IconCertificate,
    // IconBell, IconBrandWordpress, IconLinkOff, IconBuildingBroadcastTower,
    IconCloudPlus
} from '@tabler/icons-react';

// ---------------- i18n ----------------
const dict = {
    en: {
        // carousel
        servers: "Servers",
        webServices: "Web Services",
        domains: "Domains",
        certificates: "Certificates",

        // table common
        name: "Name",
        status: "Status",
        viewAll: "View All",
        itemsWithIssues: (a, b) => `Items with issues: ${a} / ${b}`,
        totalAvailable: (b) => `Total available items: ${b}`,
        noItems: "No items available",

        // sections titles
        serversTitle: "Servers",
        webServicesTitle: "Web Services Tests",
        domainsTitle: "Domains",
        certificatesTitle: "Certificates",

        // empty states + buttons
        noServersMsg: "No Servers Test Available",
        noServersDesc: "There are no servers tests to display at the moment. Try adding a new servers test or check back later.",
        addServerBtn: "Add New Server Test",

        noWebServicesMsg: "No Web Services Tests Available",
        noWebServicesDesc: "There are no web services tests to display at the moment. Try adding a new web services tests or check back later.",
        addWebServicesBtn: "Add New Web Services Test",

        noDomainsMsg: "No Domains Tests Available",
        noDomainsDesc: "There are no domains tests to display at the moment. Try adding a new domains tests or check back later.",
        addDomainsBtn: "Add New Domains Test",

        noCertificatesMsg: "No Certificates Tests Available",
        noCertificatesDesc: "There are no certificates tests to display at the moment. Try adding a new certificates tests or check back later.",
        addCertificatesBtn: "Add New Certificates Test",
    },
    ru: {
        servers: "Серверы",
        webServices: "Веб-сервисы",
        domains: "Домены",
        certificates: "Сертификаты",

        name: "Название",
        status: "Статус",
        viewAll: "Показать все",
        itemsWithIssues: (a, b) => `Проблемных элементов: ${a} / ${b}`,
        totalAvailable: (b) => `Всего доступно: ${b}`,
        noItems: "Нет доступных элементов",

        serversTitle: "Серверы",
        webServicesTitle: "Тесты веб-сервисов",
        domainsTitle: "Домены",
        certificatesTitle: "Сертификаты",

        noServersMsg: "Нет доступных тестов серверов",
        noServersDesc: "Сейчас нет тестов серверов для отображения. Добавьте новый тест или попробуйте позже.",
        addServerBtn: "Добавить тест сервера",

        noWebServicesMsg: "Нет доступных тестов веб-сервисов",
        noWebServicesDesc: "Сейчас нет тестов веб-сервисов для отображения. Добавьте новый тест или попробуйте позже.",
        addWebServicesBtn: "Добавить тест веб-сервиса",

        noDomainsMsg: "Нет доступных тестов доменов",
        noDomainsDesc: "Сейчас нет тестов доменов для отображения. Добавьте новый тест или попробуйте позже.",
        addDomainsBtn: "Добавить тест домена",

        noCertificatesMsg: "Нет доступных тестов сертификатов",
        noCertificatesDesc: "Сейчас нет тестов сертификатов для отображения. Добавьте новый тест или попробуйте позже.",
        addCertificatesBtn: "Добавить тест сертификата",
    },
    ua: {
        servers: "Сервери",
        webServices: "Веб-сервіси",
        domains: "Домени",
        certificates: "Сертифікати",

        name: "Назва",
        status: "Статус",
        viewAll: "Показати все",
        itemsWithIssues: (a, b) => `Проблемних елементів: ${a} / ${b}`,
        totalAvailable: (b) => `Всього доступно: ${b}`,
        noItems: "Немає доступних елементів",

        serversTitle: "Сервери",
        webServicesTitle: "Тести веб-сервісів",
        domainsTitle: "Домени",
        certificatesTitle: "Сертифікати",

        noServersMsg: "Немає доступних тестів серверів",
        noServersDesc: "Зараз немає тестів серверів для відображення. Додайте новий тест або спробуйте пізніше.",
        addServerBtn: "Додати тест сервера",

        noWebServicesMsg: "Немає доступних тестів веб-сервісів",
        noWebServicesDesc: "Зараз немає тестів веб-сервісів для відображення. Додайте новий тест або спробуйте пізніше.",
        addWebServicesBtn: "Додати тест веб-сервісу",

        noDomainsMsg: "Немає доступних тестів доменів",
        noDomainsDesc: "Зараз немає тестів доменів для відображення. Додайте новий тест або спробуйте пізніше.",
        addDomainsBtn: "Додати тест домену",

        noCertificatesMsg: "Немає доступних тестів сертифікатів",
        noCertificatesDesc: "Зараз немає тестів сертифікатів для відображення. Додайте новий тест або спробуйте пізніше.",
        addCertificatesBtn: "Додати тест сертифіката",
    },
};

// ---------------- components ----------------
const OwlCarousel = ({ totalData = {}, t }) => {
    const items = [
        { key: 'servers', label: t.servers, color: 'primary', icon: IconServer },
        { key: 'webservices', label: t.webServices, color: 'warning', icon: IconCloud },
        { key: 'domains', label: t.domains, color: 'danger', icon: IconGlobe },
        { key: 'certificates', label: t.certificates, color: 'success', icon: IconCertificate },
    ];

    return (
        <div className="owl-stage-outer">
            <div className="owl-stage d-flex flex-wrap dashboard-stats-grid" style={{ width: "100%" }}>
                {items.map(({ key, label, color, icon: IconComponent, singleValue }) => (
                    <div key={key} className="owl-item cloned dashboard-stats-item">
                        <div className="item">
                            <div className={`card border-0 zoom-in bg-${color}-subtle shadow-none dashboard-stats-card`}>
                                <div className="card-body">
                                    <div className="dashboard-stats-content">
                                        <div className="dashboard-stats-icon">
                                            <IconComponent size={50} color={`var(--bs-${color})`} />
                                        </div>
                                        <div className="dashboard-stats-text">
                                            <p className={`fw-semibold fs-3 text-${color} mb-1`}>{label}</p>
                                            <h5 className={`fw-semibold text-${color} mb-0`}>
                                                {totalData[key] && singleValue
                                                    ? totalData[key]
                                                    : `${totalData[`used_${key}`] ?? 0} / ${totalData[key] ?? 0}`}
                                            </h5>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const ServiceDashboardTable = ({ settings, servers = [], t }) => {
    const displayedServers = servers.slice(0, 4);

    return (
        <div className="card w-100">
            <div className="card-body">
                <div className="d-sm-flex d-block align-items-center justify-content-between mb-7">
                    <div className="mb-3 mb-sm-0">
                        <h5 className="card-title fw-semibold">{settings.name}</h5>
                        <p className="card-subtitle mb-0">
                            {t.totalAvailable(settings.totalItems || 0)}
                        </p>
                    </div>
                    {settings.item_num > 0 && (
                        <span className="badge bg-danger-subtle text-danger fw-semibold">
                            {t.itemsWithIssues(settings.item_num, settings.totalItems || 0)}
                        </span>
                    )}
                </div>

                {servers.length === 0 ? (
                    <div className="text-center py-5">
                        <h5 className="text-muted">📂 {settings.noDomainsMessage}</h5>
                        <p className="text-muted">{settings.noDomainsDescription}</p>
                        <Link to={settings.addDomainLink} className="btn btn-rounded bg-primary-subtle text-primary">
                            <IconCloudPlus /> {settings.addDomainText}
                        </Link>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="table align-middle text-nowrap mb-0">
                            <thead>
                            <tr className="text-muted fw-semibold">
                                <th scope="col">{t.name}</th>
                                <th scope="col" className="text-end">{t.status}</th>
                            </tr>
                            </thead>
                            <tbody className="border-top">
                            {displayedServers.map((server, index) => (
                                <tr key={index} className={index % 2 === 0 ? 'bg-light' : ''}>
                                    <td>
                                        <p className="mb-0 fs-3">
                                            <Link
                                                to={`${settings?.editLink?.replace('{id}', server.id) ?? '#'}`}
                                                className="text-decoration-none text-warning"
                                            >
                                                {server.name}
                                            </Link>
                                        </p>
                                    </td>
                                    <td className="text-end">
                      <span
                          className="status-dot"
                          style={{
                              display: 'inline-block',
                              width: '12px',
                              height: '12px',
                              borderRadius: '50%',
                              backgroundColor:
                                  server.status === 2 ? 'red' :
                                      server.status === 3 ? 'orange' :
                                          server.status === 1 ? 'green' : 'gray'
                          }}
                      ></span>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                        <div className="text-center mt-3">
                            <Link to={settings.viewAllLink} className="fw-semibold">{t.viewAll}</Link>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// ---------------- page ----------------
const Dashboard = ({ lang = 'en' }) => {
    const [totalData, setTotalData] = useState({});
    const [servers, setServers] = useState([]);
    const [webservices, setWebServices] = useState([]);
    const [domains, setDomains] = useState([]);
    const [certificates, setCertificates] = useState([]);
    const [pushtests, setPushTests] = useState([]);
    const [brokenlinks, setBrokenLinks] = useState([]);
    const [loadspeed, setLoadSpeed] = useState([]);
    const [wordpress, setWordpress] = useState([]);

    const t = useMemo(() => dict[lang] || dict.en, [lang]);
    const { user } = useAuth();

    const listeners = [
        { name: "dashboard", fn: "reports-total", args: {}, saveDataFN: setTotalData },
        { name: "dashboard", fn: "loadspeed", args: {}, saveDataFN: setLoadSpeed },
        { name: "dashboard", fn: "brokenlinks", args: {}, saveDataFN: setBrokenLinks },
        { name: "dashboard", fn: "pushtests", args: {}, saveDataFN: setPushTests },
        { name: "dashboard", fn: "webservices", args: {}, saveDataFN: setWebServices },
        { name: "dashboard", fn: "servers", args: {}, saveDataFN: setServers },
        { name: "dashboard", fn: "domains", args: {}, saveDataFN: setDomains },
        { name: "dashboard", fn: "certificates", args: {}, saveDataFN: setCertificates },
        { name: "dashboard", fn: "wordpress", args: {}, saveDataFN: setWordpress },
    ];

    const wsId = user?.company_id;

    useEffect(() => socketUseEffect(listeners, user), [wsId]);

    return (
        <div className="container-fluid">
            <div className="owl-carousel counter-carousel owl-theme owl-rtl owl-loaded owl-drag owl-grab">
                <OwlCarousel totalData={totalData} t={t} />
            </div>

            <div style={{ clear: "both" }} />

            <div className="row">
                <div className="col-lg-6 d-flex align-items-strech">
                    <ServiceDashboardTable
                        t={t}
                        settings={{
                            name: t.serversTitle,
                            item_num: servers?.warning ?? 0,
                            totalItems: totalData?.used_servers ?? 0,
                            noDomainsMessage: t.noServersMsg,
                            noDomainsDescription: t.noServersDesc,
                            addDomainText: t.addServerBtn,
                            addDomainLink: "/servers",
                            editLink: "/server/{id}/dashboard",
                            viewAllLink: "/servers"
                        }}
                        servers={servers?.data ?? []}
                    />
                </div>

                <div className="col-lg-6 d-flex align-items-strech">
                    <ServiceDashboardTable
                        t={t}
                        settings={{
                            name: t.webServicesTitle,
                            item_num: webservices?.warning ?? 0,
                            totalItems: totalData?.used_webservices ?? 0,
                            noDomainsMessage: t.noWebServicesMsg,
                            noDomainsDescription: t.noWebServicesDesc,
                            addDomainText: t.addWebServicesBtn,
                            addDomainLink: "/web-sites",
                            editLink: "/web-site/{id}/dashboard",
                            viewAllLink: "/web-sites"
                        }}
                        servers={webservices?.data ?? []}
                    />
                </div>
            </div>

            <div className="row">
                <div className="col-lg-6 d-flex align-items-strech">
                    <ServiceDashboardTable
                        t={t}
                        settings={{
                            name: t.domainsTitle,
                            item_num: domains?.warning ?? 0,
                            totalItems: totalData?.used_domains ?? 0,
                            noDomainsMessage: t.noDomainsMsg,
                            noDomainsDescription: t.noDomainsDesc,
                            addDomainText: t.addDomainsBtn,
                            addDomainLink: "/domains",
                            editLink: "/domains/{id}/history",
                            viewAllLink: "/domains"
                        }}
                        servers={domains?.data ?? []}
                    />
                </div>

                <div className="col-lg-6 d-flex align-items-strech">
                    <ServiceDashboardTable
                        t={t}
                        settings={{
                            name: t.certificatesTitle,
                            item_num: certificates?.warning ?? 0,
                            totalItems: totalData?.used_certificates ?? 0,
                            noDomainsMessage: t.noCertificatesMsg,
                            noDomainsDescription: t.noCertificatesDesc,
                            addDomainText: t.addCertificatesBtn,
                            addDomainLink: "/certificates",
                            editLink: "/certificates/{id}/history",
                            viewAllLink: "/certificates"
                        }}
                        servers={certificates?.data ?? []}
                    />
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
